import React, { useCallback, useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { Loader2, Download, BarChart3 } from "lucide-react";
import { adminApi } from "@/lib/adminApi";

const NAVY = "#0A2540";
const COBALT = "#1E6FD9";
const GOLD = "#C9A84C";

function fmtSeconds(s) {
    if (s == null) return "—";
    if (s < 60) return `${Math.round(s)}s`;
    if (s < 3600) return `${(s / 60).toFixed(1)}m`;
    if (s < 86400) return `${(s / 3600).toFixed(1)}h`;
    return `${(s / 86400).toFixed(1)}d`;
}

export default function AdminFunnel() {
    const today = new Date().toISOString().slice(0, 10);
    const last30 = new Date(Date.now() - 30 * 86400000).toISOString().slice(0, 10);
    const [from, setFrom] = useState(last30);
    const [to, setTo] = useState(today);
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);

    const load = useCallback(() => {
        setLoading(true);
        adminApi.reportFunnel({ from, to })
            .then(setData)
            .catch((err) => toast.error(err?.response?.data?.detail || "Couldn't load funnel."))
            .finally(() => setLoading(false));
    }, [from, to]);
    useEffect(() => { load(); }, [load]);

    const maxEntered = useMemo(() => {
        if (!data?.stages?.length) return 0;
        return Math.max(...data.stages.map((s) => s.entered || 0));
    }, [data]);

    const exportCsv = () => {
        const lines = ["Stage,Entered,% of Prior,Median Time-to-Next (seconds),Sample Size"];
        (data?.stages || []).forEach((s) => {
            lines.push([s.stage, s.entered, s.pct_of_prior, s.median_seconds_to_next ?? "", s.sample_size_for_median].join(","));
        });
        downloadBlob(lines.join("\n"), `funnel_${from}_to_${to}.csv`);
    };

    return (
        <div className="space-y-5" data-testid="report-funnel">
            <header className="flex flex-wrap items-end justify-between gap-3">
                <div>
                    <div className="mm-eyebrow flex items-center gap-1.5"><BarChart3 size={11} /> Report</div>
                    <h1 className="mm-display text-3xl text-mm-navy">Conversion Funnel</h1>
                    <p className="mt-1 text-sm text-mm-muted">Lead pipeline progression. Medians from <code className="bg-mm-bg px-1.5 py-0.5 text-xs rounded">status_history</code>.</p>
                </div>
                <div className="flex items-center gap-2">
                    <input type="date" value={from} onChange={(e) => setFrom(e.target.value)} className="rounded-md border border-mm-line px-2 py-1.5 text-sm" data-testid="funnel-from" />
                    <span className="text-xs text-mm-muted">→</span>
                    <input type="date" value={to} onChange={(e) => setTo(e.target.value)} className="rounded-md border border-mm-line px-2 py-1.5 text-sm" data-testid="funnel-to" />
                    <button onClick={exportCsv} className="mm-btn-outline !py-1.5 text-xs" data-testid="funnel-csv">
                        <Download size={12} /> CSV
                    </button>
                </div>
            </header>

            {loading ? (
                <div className="mm-card py-10 text-center"><Loader2 className="mx-auto animate-spin text-mm-navy" /></div>
            ) : (data?.stages?.length || 0) === 0 ? (
                <div className="mm-card py-10 text-center text-mm-muted">No stages in this range.</div>
            ) : (
                <div className="mm-card p-5">
                    <div className="space-y-2.5">
                        {data.stages.map((s, i) => {
                            const width = maxEntered > 0 ? Math.max(4, Math.round((s.entered || 0) / maxEntered * 100)) : 4;
                            const tone = i === data.stages.length - 1 ? GOLD : i < data.stages.length / 2 ? COBALT : NAVY;
                            return (
                                <div key={s.stage} className="grid grid-cols-[160px_1fr_120px_120px] items-center gap-3 text-sm" data-testid={`funnel-stage-${i}`}>
                                    <div className="text-right text-xs font-medium text-mm-navy">{s.stage}</div>
                                    <div className="relative h-7 rounded-full bg-mm-bg">
                                        <div
                                            className="h-full rounded-full transition-all"
                                            style={{ width: `${width}%`, background: `linear-gradient(90deg, ${tone}, ${tone}cc)` }}
                                        />
                                        <span className="absolute inset-0 flex items-center px-3 text-[11px] font-semibold text-mm-navy mix-blend-luminosity">
                                            {s.entered}
                                        </span>
                                    </div>
                                    <div className="text-right text-xs text-mm-muted tabular-nums">
                                        {s.pct_of_prior != null ? `${s.pct_of_prior}% of prior` : "—"}
                                    </div>
                                    <div className="text-right text-xs text-mm-muted tabular-nums" title={`Based on ${s.sample_size_for_median} historical transitions`}>
                                        median → {fmtSeconds(s.median_seconds_to_next)}
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                    <p className="mt-4 text-[11px] italic text-mm-muted">
                        Pipeline order: {(data.pipeline_order || []).join(" → ")}
                    </p>
                </div>
            )}
        </div>
    );
}

function downloadBlob(data, filename) {
    const blob = new Blob([data], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url; a.download = filename;
    document.body.appendChild(a); a.click(); document.body.removeChild(a);
    URL.revokeObjectURL(url);
}
